import React, { useEffect, useMemo, useState } from "react";
import { FiCheck } from "react-icons/fi";
import { COUNTRIES } from "../../data/locationTaxonomy";
import { countryCodeToFlag, getDialCodeForCountry } from "../../utils/phoneUtils";
import BottomSheet from "./BottomSheet";
import "./DialCodePickerSheet.css";

export default function DialCodePickerSheet({
  open,
  onClose,
  value,
  selectedCountryCode,
  onSelect,
  title = "Country code",
}) {
  const [query, setQuery] = useState("");

  const options = useMemo(
    () => COUNTRIES.map((country) => ({
      code: country.code,
      name: country.name,
      dialCode: getDialCodeForCountry(country.code),
      flag: countryCodeToFlag(country.code),
    })).sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filtered = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return options;
    return options.filter(
      (option) =>
        option.name.toLowerCase().includes(trimmed)
        || option.dialCode.includes(trimmed)
        || option.code.toLowerCase().includes(trimmed),
    );
  }, [options, query]);

  const handleSelect = (option) => {
    onSelect?.(option);
    onClose?.();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={title}>
      <div className="dial-code-picker__search-wrap">
        <input
          type="search"
          className="dial-code-picker__search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search country or code"
          autoComplete="off"
        />
      </div>

      <ul className="dial-code-picker__list" role="listbox" aria-label={title}>
        {filtered.length === 0 ? (
          <li className="dial-code-picker__empty">No results</li>
        ) : (
          filtered.map((option) => {
            const selected = selectedCountryCode
              ? option.code === selectedCountryCode
              : option.dialCode === value;
            return (
              <li key={option.code} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`dial-code-picker__option${selected ? " dial-code-picker__option--selected" : ""}`}
                  onClick={() => handleSelect(option)}
                >
                  <span className="dial-code-picker__option-main">
                    <span className="dial-code-picker__flag" aria-hidden>{option.flag}</span>
                    <span className="dial-code-picker__name">{option.name}</span>
                  </span>
                  <span className="dial-code-picker__meta">
                    <span className="dial-code-picker__dial">{option.dialCode}</span>
                    {selected ? <FiCheck aria-hidden /> : null}
                  </span>
                </button>
              </li>
            );
          })
        )}
      </ul>
    </BottomSheet>
  );
}
